import React from 'react';
import {
  unattendedChip,
  UNATTENDED_SOURCE_LABELS,
  type AsuntosSinAtenderEntry,
} from '@zunelo/shared';
import { useZunelo } from '../../context/ZuneloContext';
import { formatRelativeTime } from '../../domain/types';
import { ErrorState, Button } from '../../ui/common';

function sourceTone(source: AsuntosSinAtenderEntry['source']): string {
  switch (source) {
    case 'slack':
      return 'var(--z-slack, #36c5f0)';
    case 'jira':
      return 'var(--z-jira, #2684ff)';
    case 'github':
      return 'var(--z-github, #f78166)';
    case 'daily':
      return 'var(--z-accent)';
    default:
      return 'var(--z-muted)';
  }
}

function EntryRow({ entry }: { entry: AsuntosSinAtenderEntry }): React.ReactElement {
  const { toggleReminder, refreshUnattendedInbox } = useZunelo();
  const chip = unattendedChip(entry);
  const timeLabel = formatRelativeTime(entry.updatedAt);

  const openEntry = () => {
    if (entry.permalink) {
      window.open(entry.permalink, '_blank', 'noopener,noreferrer');
    } else if (entry.pendingAction?.href) {
      window.open(entry.pendingAction.href, '_blank', 'noopener,noreferrer');
    }
  };

  // The Daily reminder row's "Marcar como hecho" has no href — it is the
  // reminder-completion mutation, not a link. Any other pendingAction with
  // no href and no recognized handler is not rendered at all (see below):
  // a visible control MUST NOT be inert. See
  // openspec/changes/provider-item-interaction-ux (honest controls).
  const dailyReminderId = entry.source === 'daily' ? entry.refId.replace(/^daily:/, '') : undefined;
  const handleMarkDone = dailyReminderId
    ? () => {
        void toggleReminder(dailyReminderId).then(() => refreshUnattendedInbox(true));
      }
    : undefined;

  return (
    <div
      data-testid={`unattended-entry-${entry.id}`}
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        gap: 8,
        padding: '10px 12px',
        borderRadius: 12,
        background: 'var(--z-surface)',
        border: 'none',
      }}
    >
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: sourceTone(entry.source),
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '-0.01em',
              color: 'var(--z-muted-2)',
              textTransform: 'uppercase',
            }}
          >
            {UNATTENDED_SOURCE_LABELS[entry.source]}
          </span>
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10.5,
              padding: '1px 6px',
              borderRadius: 6,
              background: 'var(--z-fill-2)',
              color: 'var(--z-dim)',
            }}
          >
            {timeLabel}
          </span>
        </div>
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--z-text)',
            lineHeight: 1.3,
            overflowWrap: 'anywhere',
          }}
        >
          {entry.title}
        </span>
        {entry.subtitle && (
          <span
            style={{
              fontSize: 12,
              color: 'var(--z-muted)',
              lineHeight: 1.4,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {entry.subtitle}
          </span>
        )}
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 2 }}
        >
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              padding: '2px 7px',
              borderRadius: 999,
              background:
                chip.tone === 'warn'
                  ? 'var(--z-warn-soft, rgba(255, 193, 7, 0.14))'
                  : 'var(--z-fill-2)',
              color: chip.tone === 'warn' ? 'var(--z-warn-copy, #b8860b)' : 'var(--z-muted)',
            }}
          >
            {chip.label}
          </span>
          {entry.dueAt && (
            <span
              style={{
                fontSize: 10.5,
                padding: '2px 7px',
                borderRadius: 999,
                background: 'var(--z-fill-2)',
                color: 'var(--z-dim)',
              }}
            >
              Vence {formatRelativeTime(entry.dueAt)}
            </span>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {entry.pendingAction?.href || entry.permalink ? (
          <Button variant="ghost" size="sm" onClick={openEntry}>
            {entry.pendingAction?.label ?? 'Abrir'}
          </Button>
        ) : handleMarkDone ? (
          <Button
            variant="ghost"
            size="sm"
            data-testid={`unattended-mark-done-${entry.id}`}
            onClick={handleMarkDone}
          >
            {entry.pendingAction?.label ?? 'Marcar como hecho'}
          </Button>
        ) : null}
        {entry.secondaryActions
          ?.filter((action) => action.href)
          .map((action, idx) => (
            <Button
              key={`${action.label}-${idx}`}
              variant="ghost"
              size="sm"
              onClick={() => window.open(action.href, '_blank', 'noopener,noreferrer')}
            >
              {action.label}
            </Button>
          ))}
      </div>
    </div>
  );
}

function EmptyInbox({ onOpenThreads }: { onOpenThreads?: () => void }): React.ReactElement {
  return (
    <div
      style={{
        padding: '32px 20px',
        textAlign: 'center',
        borderRadius: 14,
        background: 'var(--z-surface)',
        border: 'none',
        color: 'var(--z-dim)',
        fontSize: 13,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        alignItems: 'center',
      }}
    >
      <span>No tienes asuntos sin atender ahora mismo.</span>
      {onOpenThreads && (
        <Button variant="ghost" size="sm" onClick={onOpenThreads}>
          Ver hilos de Slack
        </Button>
      )}
    </div>
  );
}

export function UnattendedInboxPanel({
  onOpenThreads,
}: {
  onOpenThreads?: () => void;
}): React.ReactElement {
  const { unattendedInbox, unattendedInboxLoading, refreshUnattendedInbox, errors } = useZunelo();
  const error = errors.unattendedInbox;

  if (error) {
    return (
      <ErrorState
        title="Error al cargar asuntos"
        message={error}
        onRetry={() => refreshUnattendedInbox(true)}
        isRetrying={unattendedInboxLoading}
      />
    );
  }

  const entries = unattendedInbox.entries;
  const { slack, jira, github, daily } = unattendedInbox.counts;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, width: '100%', minWidth: 0 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {(
            [
              ['Slack', slack],
              ['Jira', jira],
              ['GitHub', github],
              ['Daily', daily],
            ] as Array<[string, number]>
          ).map(([label, count]) => (
            <span
              key={String(label)}
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '3px 9px',
                borderRadius: 999,
                background: 'var(--z-fill-2)',
                color: count > 0 ? 'var(--z-text)' : 'var(--z-dim)',
              }}
            >
              {label} {count}
            </span>
          ))}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => refreshUnattendedInbox(true)}
          disabled={unattendedInboxLoading}
        >
          {unattendedInboxLoading ? 'Actualizando…' : 'Actualizar'}
        </Button>
      </div>

      {entries.length === 0 ? (
        <EmptyInbox onOpenThreads={onOpenThreads} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {entries.map((entry) => (
            <EntryRow key={entry.id} entry={entry} />
          ))}
        </div>
      )}
    </div>
  );
}
